import React, { useState } from 'react';
import { DateRange } from '../../types/trends';

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange
}) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const presetOptions: Array<{
    value: DateRange['preset'];
    label: string;
  }> = [
    { value: 'last3months', label: 'Last 3 months' },
    { value: 'last6months', label: 'Last 6 months' },
    { value: 'last1year', label: 'Last year' },
    { value: 'last2years', label: 'Last 2 years' },
    { value: 'all', label: 'All time' },
  ];

  const handlePresetChange = (preset: DateRange['preset']) => {
    const option = presetOptions.find(opt => opt.value === preset);
    if (option) {
      onChange({
        preset,
        label: option.label
      });
      setShowCustom(false);
    }
  };

  const handleCustomRange = () => {
    if (customStart && customEnd) {
      onChange({
        startDate: customStart,
        endDate: customEnd,
        label: `${new Date(customStart).toLocaleDateString()} - ${new Date(customEnd).toLocaleDateString()}`
      });
      setShowCustom(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Date Range</h3>
      
      {/* Preset Options */}
      <div className="space-y-2">
        {presetOptions.map(option => (
          <label
            key={option.value}
            className="flex items-center p-2 rounded cursor-pointer hover:bg-gray-50"
          >
            <input
              type="radio"
              name="dateRange"
              checked={value.preset === option.value}
              onChange={() => handlePresetChange(option.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-3 text-sm text-gray-900">{option.label}</span>
          </label>
        ))}
        
        {/* Custom Range Option */}
        <label className="flex items-center p-2 rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="dateRange"
            checked={showCustom || (!value.preset && !!value.startDate)}
            onChange={() => setShowCustom(true)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <span className="ml-3 text-sm text-gray-900">Custom range</span>
        </label>
      </div>

      {/* Custom Date Inputs */}
      {showCustom && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              min={customStart}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleCustomRange}
            disabled={!customStart || !customEnd}
            className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Apply Custom Range
          </button>
        </div>
      )}

      {/* Current Selection Display */}
      <div className="mt-4 pt-4 border-t">
        <p className="text-xs text-gray-500">Currently showing:</p>
        <p className="text-sm font-medium text-gray-900">{value.label}</p>
      </div>
    </div>
  );
};